import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import React from "react";
import { Input } from "semantic-ui-react";
import { compareLanguages } from "utilities/translationUtils";

interface Props {
  allLanguages: server.dto.Language[];
  category?: server.dto.ArticleCategory;
  onCancel: () => void;
  onSave: (translations: server.dto.SaveArticleCategoryTranslationInstruction[]) => void;
}

type CategoryTranslation = {
  title: string;
  language: server.dto.Languages;
}

const CategoryRowEdit = ({ category, onCancel, onSave, allLanguages }: Props) => {
  const [translations, setTranslations] = React.useState<CategoryTranslation[]>(() => {
    if (category == null) {
      return [...allLanguages]
        .sort((a, b) => compareLanguages(a.value, b.value))
        .map<CategoryTranslation>(language => ({ language: language.value, title: "" }));
    }

    const categoryTranslations = [...category.translations].sort((a, b) => compareLanguages(a.language, b.language));
    const missingLanguages = allLanguages.filter(language => !categoryTranslations.some(translation => translation.language === language.value));
    const existingTranslations = category.translations.map<CategoryTranslation>(translation => ({ language: translation.language, title: translation.title }));

    return [
      ...existingTranslations,
      ...missingLanguages.map<CategoryTranslation>(language => ({ language: language.value, title: "" }))
    ].sort((a, b) => compareLanguages(a.language, b.language));
  });

  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const [highlightErrorOnEnglish, setHighlightErrorOnEnglish] = React.useState<boolean>(false);

  const updateTranslation = (language: server.dto.Languages, title: string) => {
    setTranslations(prevTranslations => prevTranslations.map(translation => translation.language === language ? { ...translation, title } : translation));
  }

  const onSaveClicked = () => {
    if (translations.some(t => t.language === "english" && t.title.trim() === "")) {
      setHighlightErrorOnEnglish(true);
      return;
    }

    onSave(translations
      .map(t => ({ language: t.language, title: t.title }))
      .filter(t => t.title.trim() !== "")
    );
  }

  return <tr>
    {translations.map(translation =>
      <td key={translation.language}>
        <Input
          ref={(input) => input && isFirstRender.current && translation.language === "english" && input.focus()}
          value={translation.title}
          onChange={(_, { value }) => updateTranslation(translation.language, value)}
          placeholder={translation.language}
          error={highlightErrorOnEnglish && translation.language === "english" && translation.title.trim() === ""}
          onKeyDown={e => e.key === "Enter" && onSaveClicked()}
        />
      </td>)}

    <td>
      <div className="flex gap">
        <ButtonPrimary label={RESX.Buttons.btnSave} onClick={onSaveClicked} />
        <ButtonSecondary label={RESX.Buttons.btnCancel} onClick={onCancel} />
      </div>
    </td>
  </tr>
}

export default CategoryRowEdit;